import { PaymentMethod } from '../services/paymentService';
import env from '../config/env';

// Card brand utilities
export const getCardBrandIcon = (brand: string): string => {
  const icons: { [key: string]: string } = {
    visa: '💳',
    mastercard: '💳',
    amex: '💳',
    discover: '💳',
    diners: '💳',
    jcb: '💳',
    unionpay: '💳',
    unknown: '💳'
  };
  
  return icons[brand.toLowerCase()] || icons.unknown;
};

export const formatCardBrand = (brand: string): string => {
  const brands: { [key: string]: string } = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
    unknown: 'Desconocida'
  };
  
  return brands[brand.toLowerCase()] || brands.unknown;
};

export const getCardBrandColor = (brand: string): string => {
  const colors: { [key: string]: string } = {
    visa: 'text-blue-600',
    mastercard: 'text-red-600',
    amex: 'text-green-600',
    discover: 'text-orange-600',
    diners: 'text-purple-600',
    jcb: 'text-indigo-600',
    unionpay: 'text-pink-600',
    unknown: 'text-gray-600'
  };
  
  return colors[brand.toLowerCase()] || colors.unknown;
};

// Payment method utilities
export const formatExpiryDate = (month: number, year: number): string => {
  const paddedMonth = month.toString().padStart(2, '0');
  return `${paddedMonth}/${year}`;
};

export const isCardExpired = (month: number, year: number): boolean => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  
  if (year < currentYear) return true;
  if (year === currentYear && month < currentMonth) return true;
  
  return false;
};

export const isCardExpiringSoon = (month: number, year: number, monthsAhead: number = 3): boolean => {
  const expiryDate = new Date(year, month - 1);
  const warningDate = new Date();
  warningDate.setMonth(warningDate.getMonth() + monthsAhead);
  
  return expiryDate <= warningDate;
};

export const getCardExpiryStatus = (month: number, year: number): 'expired' | 'expiring_soon' | 'valid' => {
  if (isCardExpired(month, year)) return 'expired';
  if (isCardExpiringSoon(month, year)) return 'expiring_soon';
  return 'valid';
};

// Payment method sorting and filtering
export const sortPaymentMethods = (methods: PaymentMethod[]): PaymentMethod[] => {
  return [...methods].sort((a, b) => {
    // Default card first
    if (a.isDefault && !b.isDefault) return -1;
    if (!a.isDefault && b.isDefault) return 1;
    
    // Then by creation date (newest first) if available
    if (a.createdAt && b.createdAt) {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    }
    
    // Finally by brand and last4
    const brandCompare = a.brand.localeCompare(b.brand);
    if (brandCompare !== 0) return brandCompare;
    
    return a.last4.localeCompare(b.last4);
  });
};

export const filterValidPaymentMethods = (methods: PaymentMethod[]): PaymentMethod[] => {
  return methods.filter(method => !isCardExpired(method.expMonth, method.expYear));
};

// Amount formatting
export const formatAmount = (amount: number, currency?: string): string => {
  const useCurrency = currency || env.PAYMENT_CURRENCY;
  
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: useCurrency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount / 100);
};

export const formatAmountForDisplay = (amount: number): string => {
  return (amount / 100).toFixed(2);
};

// Validation utilities
export const validateCardNumber = (cardNumber: string): boolean => {
  // Remove spaces and non-digits
  const cleaned = cardNumber.replace(/\D/g, '');
  
  // Check length
  if (cleaned.length < 13 || cleaned.length > 19) return false;
  
  // Luhn algorithm
  let sum = 0;
  let isEven = false;
  
  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = parseInt(cleaned.charAt(i), 10);
    
    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    
    sum += digit;
    isEven = !isEven;
  }
  
  return sum % 10 === 0;
};

export const validateExpiryDate = (month: number, year: number): boolean => {
  if (month < 1 || month > 12) return false;
  if (year < new Date().getFullYear()) return false;
  
  return !isCardExpired(month, year);
};

export const validateCVV = (cvv: string, cardBrand?: string): boolean => {
  const cleaned = cvv.replace(/\D/g, '');
  
  if (cardBrand === 'amex') {
    return cleaned.length === 4;
  }
  
  return cleaned.length === 3;
};

// Error handling utilities
export const getPaymentErrorMessage = (error: any): string => {
  if (typeof error === 'string') return error;
  
  if (error?.message) return error.message;
  
  if (error?.code) {
    const errorMessages: { [key: string]: string } = {
      'card_declined': 'Tu tarjeta fue rechazada. Intenta con otra tarjeta.',
      'expired_card': 'Tu tarjeta ha expirado. Usa una tarjeta válida.',
      'incorrect_cvc': 'El código de seguridad es incorrecto.',
      'processing_error': 'Error al procesar el pago. Inténtalo de nuevo.',
      'insufficient_funds': 'Fondos insuficientes en tu tarjeta.',
      'authentication_required': 'Se requiere autenticación adicional.',
      'generic_decline': 'El pago fue rechazado. Contacta a tu banco.',
    };
    
    return errorMessages[error.code] || 'Error desconocido en el pago.';
  }
  
  return 'Error desconocido en el pago.';
};

// Stripe Elements styling
export const getStripeElementsTheme = () => ({
  appearance: {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#f6cf02',
      colorBackground: '#ffffff',
      colorText: '#0d0d0d',
      colorDanger: '#ef4444',
      colorSuccess: '#10b981',
      fontFamily: 'Inter, system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
      focusBoxShadow: '0 0 0 2px rgba(246, 207, 2, 0.2)',
    },
    rules: {
      '.Input': {
        padding: '12px',
        fontSize: '16px',
        lineHeight: '1.5',
      },
      '.Input:focus': {
        borderColor: '#f6cf02',
        boxShadow: '0 0 0 2px rgba(246, 207, 2, 0.2)',
      },
      '.Label': {
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: '8px',
      },
    },
  },
});

export const getCardElementOptions = () => ({
  style: {
    base: {
      fontSize: '16px',
      color: '#0d0d0d',
      fontFamily: 'Inter, system-ui, sans-serif',
      lineHeight: '1.5',
      '::placeholder': {
        color: '#6b7280',
      },
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
    complete: {
      color: '#10b981',
      iconColor: '#10b981',
    },
  },
  hidePostalCode: false,
  iconStyle: 'solid' as const,
});